import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuthStore } from '@/stores/authStore';
import { useToast } from '@/hooks/use-toast';
import { mutationLimiter, checkRateLimit } from '@/lib/rateLimiter';
import type { Tables, TablesInsert, TablesUpdate } from '@/integrations/supabase/types';

export type Meeting = Tables<'meetings'>;
export type MeetingInsert = TablesInsert<'meetings'>;
export type MeetingUpdate = TablesUpdate<'meetings'>;

export const PLATFORMS = [
  { value: 'zoom', label: 'Zoom', icon: '📹' },
  { value: 'meet', label: 'Google Meet', icon: '🟢' },
  { value: 'teams', label: 'Teams', icon: '🟣' },
  { value: 'phone', label: 'Phone', icon: '📞' },
  { value: 'in_person', label: 'In-Person', icon: '🏢' },
];

export const DURATIONS = [15, 30, 45, 60, 90, 120];

export function useMeetings() {
  const user = useAuthStore((s) => s.user);
  return useQuery({
    queryKey: ['meetings', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('meetings')
        .select('*')
        .eq('user_id', user.id)
        .order('date_time', { ascending: true });
      if (error) throw error;
      return data as Meeting[];
    },
    enabled: !!user,
    retry: 2,
    staleTime: 30_000,
  });
}

export function useCreateMeeting() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (m: Omit<MeetingInsert, 'user_id'>) => {
      checkRateLimit(mutationLimiter, 'createMeeting');
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase.from('meetings').insert({ ...m, user_id: user.id }).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['meetings'] }); toast({ title: 'Meeting created' }); },
    onError: (e) => toast({ title: 'Error creating meeting', description: e.message, variant: 'destructive' }),
  });
}

export function useUpdateMeeting() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async ({ id, ...updates }: MeetingUpdate & { id: string }) => {
      checkRateLimit(mutationLimiter, 'updateMeeting');
      const { data, error } = await supabase.from('meetings').update({ ...updates, updated_at: new Date().toISOString() }).eq('id', id).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['meetings'] }); toast({ title: 'Meeting updated' }); },
    onError: (e) => toast({ title: 'Error updating meeting', description: e.message, variant: 'destructive' }),
  });
}

export function useDeleteMeeting() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (id: string) => {
      checkRateLimit(mutationLimiter, 'deleteMeeting');
      const { error } = await supabase.from('meetings').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['meetings'] }); toast({ title: 'Meeting deleted' }); },
    onError: (e) => toast({ title: 'Error deleting meeting', description: e.message, variant: 'destructive' }),
  });
}
